import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import VenueCard from "../components/VenueCard";
import SEO from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const USER_ID = "guest-user";

export default function FavoritesPage() {
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchFavorites = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/favorites/${USER_ID}`);
      setFavorites(response.data);
    } catch (error) {
      console.debug("Favorites fetch skipped:", error.message);
      // Non-critical - user can still use the app without favorites
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);

  return (
    <div className="min-h-screen bg-surface">
      <SEO 
        title="My Favourite Workspaces"
        description="View your saved favourite remote work venues. Keep track of cafes, pubs, hotels and coworking spaces you want to visit."
        canonical="/favorites"
      />
      <Header />
      
      <div className="pt-16 px-4 md:px-8 py-6 max-w-6xl mx-auto">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-gray-600 hover:text-brand mb-4" data-testid="back-button">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center">
            <Heart className="w-6 h-6 text-red-500 fill-red-500" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Favorites</h1>
            <p className="text-sm text-gray-500">{loading ? "Loading..." : `${favorites.length} saved`}</p>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => <div key={i} className="bg-gray-100 rounded-xl h-64 animate-pulse" />)}
          </div>
        ) : favorites.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <Heart className="w-8 h-8 text-gray-400" />
            </div>
            <h2 className="text-lg font-medium text-gray-900 mb-2">No favorites yet</h2>
            <p className="text-sm text-gray-500 mb-4">Start exploring and save your favorite workspaces!</p>
            <button onClick={() => navigate('/browse')} className="btn-primary" data-testid="browse-venues-btn">Browse</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {favorites.map((venue, index) => (
              <motion.div key={venue.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}>
                <VenueCard venue={venue} />
              </motion.div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
